import { filterXSS } from "xss";
import { Timeout, isIOS, isAndroid } from "../util/Misc";
import { Constants } from "../model/Constants";
import Message from "../model/Message";
import BubbleType from "../model/BubbleType";
import { inputitemRender } from "./templates/renders/inputitem";

const InputItem: any = {
    tagName: "input-item",
    renderer: inputitemRender,
    initialize(options) {
        const app = options.$app;
        const constants = options.constants;
        const footerConfig = app.getUIConfig().footerConfig || {};
        constants.config = footerConfig.footerSettings || {};
        constants.inputStyles = footerConfig.inputStyles;
    },
    created() {
        this.disableTouchTM = null;

        /**
         * @type {HTMLInputElement} customer input element
         */
        this.input = this.el.firstElementChild;

        /**
         * typing or not typing state
         * @type {boolean}
         */
        this.isTyping = false;

        /**
         * is using IME pad or not
         * @type {boolean}
         */
        this.isComposing = false;
        this.lastKeyPressed = null;
        this.sdkInstance = this.$app.getSDKInstance();
        this.footerArea = document.querySelector("footer-area");
        this.cont = this.footerArea.firstElementChild;
        this.extendedFooter = this.cont.querySelector(".messaging-footer-extended");

        if (isIOS()) {
            this.input.classList.add("ios");
        }

        if (this.config.autoGrow || !this.config.multiline) {
            this.input.setAttribute("rows", "1");
        }

        const inputElement = this.el.querySelector("textarea");
        inputElement.addEventListener(
            "compositionstart",
            this.compositionHandler.bind(this)
        );
    },

    /**
     * invokes the @see {@link onNewCustomerMessage} callback when user submits
     * @param {Event} e
     */
    sendCustomerMessage(e) {
        let text = this.input.value.trim();

        // If enabled remove any Js execution from messages
        if (this.$app.getSDKInstance().getRemoveJSInMessages()) {
            text = filterXSS(text);
        }

        if (text !== "") {
            if (text.toLowerCase() == "/chatinfo") {
                this.resetTyping();
                const chatParams = this.$app.getSDKInstance().getChatParams();
                const chatInfo = {
                    EngagementID: chatParams.chatID,
                    BusinessUnitID: chatParams.businessUnitID,
                    BusinessUnit: chatParams.businessUnitName,
                    "Agent ID": chatParams.agentID,
                    "Business Rule": chatParams.chatTitle,
                    "Agent Group ID": chatParams.agId,
                    "Agent Group": chatParams.agentGroupName,
                    "Page Marker": chatParams.launchPageMarker,
                };

                let info = "<b>Chat Info</b> <br>";

                for (const key in chatInfo) {
                    info += `${key}: ${chatInfo[key]} <br>`;
                }

                const msg = new Message({
                    bubbleType: BubbleType.SYSTEM_MESSAGE,
                    messageText: info,
                    isAuto: false,
                    timestamp: Date.now(),
                });
                this.$app.addToUI(msg);
            } else {
                this.resetTyping();
                this.$app.onNewCustomerBubble(text);
            }
        }

        if (this.saveHeight) {
            this.cont.style.height = `${this.saveHeight}px`;
            this.footerMain.style.height = `${this.saveHeight - this.fHeightOffset}px`;
            this.$app.shoutui.adjustTranscriptBottom(this.saveHeight);
            this.saveHeight = undefined;
            this.input.style.height = "5px";
            const sh = this.input.scrollHeight;
            this.input.style.height = `${sh}px`;
        }

        if (!this.hasFocus()) {
            this.input.focus();
        }

        if (e) {
            e.preventDefault();
        }
    },
    /**
     * @param {HTMLInputElement} event
     * Handles input field touchstart event.
     */
    onInputTouchStart(event) {
        this.androidDisableTouchActionCSS(event);
    },

    androidDisableTouchActionCSS(event) {
        const deviceType = this.$app.getSDKInstance().getDeviceType();

        if (isAndroid() && deviceType === "Phone" && v3Lander.isWebSDK) {
            this.disableTouchActionZoomForAMoment(event.target, 1000);
        }
    },

    /**
     * Disable touch-action zoom for a "short" time.
     *
     * Reason:
     * Chrome 98 on android mobile has a zoom issue on touch events
     * this causes an extrem CSS zoom for the main page.
     * on input focus. This solution disables the
     */
    disableTouchActionZoomForAMoment(element, momentTimeMs) {
        if (this.disableTouchTM) {
            clearTimeout(this.disableTouchTM);
            this.disableTouchTM = null;
        }

        element.setAttribute("style", "touch-action: pan-x pan-y");
        this.disableTouchTM = setTimeout(() => {
            element.setAttribute("style", "touch-action: inherit");
        }, momentTimeMs);
    },

    /**
     * @param {HTMLInputElement} event
     * Invokes {@link MessagingTopBar} and {@link Transcript} to remove the material layout content
     */
    onInputFocus(event) {
        this.androidDisableTouchActionCSS(event);

        const messagingTopBar: any =
            document.querySelector("messaging-top-bar");
        messagingTopBar.hideMaterial();
        const transcriptArea: any = document.querySelector("transcript-area");
        transcriptArea.expandArea();

        if (isIOS()) {
            this.$app.getBridge().onVirtualKeyboardOpen();
        }

        const value = event.target.value;
        const inputElement = event.target;

        /*
         * For IE Check for value to be same as placeholder
         * clear it before user enters new value to
         * Provide the placehloder behaviour which is absent in IE
         */
        if (value === inputElement.getAttribute("placeholder")) {
            event.target.value = "";
        }
    },

    onInputBlurred() {
        if (isIOS()) {
            this.$app.getBridge().onVirtualKeyboardHidden();
        }
        this.isComposing = false;
    },

    /**
     * @param {HTMLInputElement} event
     * @return {boolean}
     */
    onInput(event) {
        if (this.config.multiline && this.config.autoGrow) {
            this.addDynamic(event);
        }
        return this.watchCustomerTyping(event);
    },

    /**
     * @param {KeyboardEvent} event
     * @return {boolean}
     */
    onKeyPress(event) {
        return this.watchCustomerTyping(event);
    },

    /**
     * @param {KeyboardEvent} event
     * @return {boolean}
     */
    onKeyUp(event) {
        this.compositionHandler(event);
        return this.watchCustomerTyping(event);
    },

    /**
     * @param {KeyboardEvent} event
     * @return {boolean}
     */
    onPaste(event) {
        return this.watchCustomerTyping(event);
    },

    /**
     * @param {CompositionEvent} event
     * @return {boolean}
     */
    compositionHandler(event) {
        if (event.type === "compositionstart") {
            this.isComposing = true;
        }
        if (this.isComposing) {
            let keyCode = event.charCode || event.keyCode;

            if (event.keyCode === Constants.KEY_PROCESS) {
                keyCode =
                    event.code === "Enter" ? Constants.KEY_ENTER : keyCode;
            }

            if (
                keyCode === Constants.KEY_ENTER &&
                this.lastKeyPressed === Constants.KEY_ENTER &&
                !event.isComposing
            ) {
                this.isComposing = false;
            }

            if (
                event.keyCode !== Constants.KEY_PROCESS ||
                keyCode === Constants.KEY_ENTER
            ) {
                this.lastKeyPressed =
                    keyCode === Constants.KEY_ENTER && event.isComposing
                        ? null
                        : keyCode;
            }
        }
    },

    /**
     * Handles customer typing and invoke sdkInstance to send the typing state
     * @param {Event} ke
     * @return {boolean}
     */
    watchCustomerTyping(ke) {
        const keyCode = !ke.charCode ? ke.keyCode : ke.charCode;

        // sends customer message when user press enter key
        if (
            !this.isComposing &&
            keyCode === Constants.KEY_ENTER &&
            !ke.altKey &&
            !ke.ctrlKey &&
            !ke.shiftKey
        ) {
            ke.preventDefault();
            this.sendCustomerMessage();
            return false;
        }

        /*
         * if(this.sdkInstance.isChatQueued() || !this.sdkInstance.isChatInProgress()) {
         *  return true;
         * }
         */

        // sends customer typing activity state for first time
        if (!this.isTyping) {
            this.isTyping = true;
            this.sdkInstance.sendActivityMessage(
                this.sdkInstance.ACTIVITY_STATE.START_TYPING
            );

            // change the send button state from disabled to enabled
            this.footerArea.toggleSendButtonState(true);
        }

        if (this.input.value === "") {
            this.isTyping = false;

            // change the send button state to disabled if user clear of all text in the input
            this.footerArea.toggleSendButtonState(false);
        }

        if (!this.typingTimeout || this.typingTimeout.executed()) {
            // Create a timeout that will be executed when user is idle for give time
            this.typingTimeout = Timeout.instantiate(() => {
                this.sdkInstance.sendActivityMessage(
                    this.sdkInstance.ACTIVITY_STATE.STOP_TYPING
                );
                this.isTyping = false;
            }, this.config.typingTimeout || 4000);
        } else {
            // restart the typing timer as user continues to enter text
            this.typingTimeout.restart();
        }
        return true;
    },

    /**
     * reset typing state after customer sends a message
     */
    resetTyping() {
        if (this.typingTimeout) {
            this.typingTimeout.clear(false);
        }

        this.isTyping = false;
        this.input.value = "";

        // change the send button state to disabled
        this.footerArea.toggleSendButtonState(false);
    },
    disable() {
        this.input.setAttribute("disabled", true);
        this.resetTyping();
    },

    enable() {
        this.input.removeAttribute("disabled");
    },

    focus() {
        this.input.focus();
    },
    hasFocus() {
        return this.input === document.activeElement;
    },
    removed() {
        if (this.typingTimeout) {
            this.typingTimeout.clear(true);
        }
    },
    clear() {
        this.input.oninput = null;
        this.input.onkeypress = null;
        this.input.onkeyup = null;
        this.input.onpaste = null;
    },
    addDynamic(e) {
        if (!this.saveHeight) {
            this.saveHeight = this.cont.offsetHeight;
            this.footerMain = this.cont.firstElementChild;
            this.fHeightOffset = this.saveHeight - this.footerMain.offsetHeight;
            this.pHeight =
                parseInt(
                    window.getComputedStyle(this.footerMain).paddingTop || "0",
                    10
                ) +
                parseInt(
                    window.getComputedStyle(this.footerMain).paddingBottom || "0",
                    10
                ) +
                parseInt(
                    window.getComputedStyle(this.footerMain).borderTopWidth || "0",
                    10
                ) +
                parseInt(
                    window.getComputedStyle(this.footerMain).borderBottomWidth || "0",
                    10
                );
            this.pHeight +=
                parseInt(window.getComputedStyle(e.target).marginTop || "0", 10) +
                parseInt(window.getComputedStyle(e.target).marginBottom || "0", 10);
        }

        // setting height to "5px" is a hack to get an accurate scrollHeight
        e.target.style.height = "5px";
        const scrollHeight = e.target.scrollHeight;
        e.target.style.height = `${scrollHeight}px`;

        if (
            e.target.offsetHeight + this.pHeight >=
            this.saveHeight - this.fHeightOffset
        ) {
            // container height is increased by 2px to highlight focus outline correctly
            this.cont.style.height = `${e.target.offsetHeight + this.pHeight + this.fHeightOffset + 2}px`;
            this.footerMain.style.height = `${this.cont.offsetHeight - this.fHeightOffset}px`;
            this.$app.shoutui.adjustTranscriptBottom(this.cont.offsetHeight);
        } else {
            this.cont.style.height = `${this.saveHeight}px`;
            this.footerMain.style.height = `${this.cont.offsetHeight - this.fHeightOffset}px`;
            this.$app.shoutui.adjustTranscriptBottom(this.saveHeight);
        }
    },
    insertEmoji(text) {
        text = text || ""; // eslint-disable-line no-param-reassign

        if (document.selection) {
            const sel = document.selection.createRange();
            sel.text = text;
        } else if (
            this.input.selectionStart ||
            this.input.selectionStart === 0
        ) {
            // Others
            const startPos = this.input.selectionStart;
            const endPos = this.input.selectionEnd;
            this.input.value =
                this.input.value.substring(0, startPos) +
                text +
                this.input.value.substring(endPos, this.input.value.length);
            this.input.selectionStart = startPos + text.length;
            this.input.selectionEnd = startPos + text.length;
        } else {
            this.input.value += text;
        }

        const e = document.createEvent("HTMLEvents");
        e.initEvent("keypress", false, true);
        this.input.dispatchEvent(e);
        this.input.focus();
    },
};

export default (appInstance) => {
    InputItem.$app = appInstance;
    InputItem.constants = {};
    return InputItem;
};
